<!-- Component for displaying the logout modal -->
<template>
	<PrecoaModal
		v-if="useFeatureService().isEnabled(Feature.CloudSync)"
		:open="modelValue"
		@update:open="(value) => emit('update:modelValue', value ?? false)"
		@submit="handleConfirm"
		class="sm:w-auto! md:max-w-md"
	>
		<template #default>
			<div class="flex flex-col items-center text-center">
				<ExclamationTriangleIcon class="mb-3 h-10 w-10 text-red-400" />

				<h2 class="mb-2 text-2xl font-bold text-red-700">Wait!</h2>

				<i18n-t
					keypath="auth.newWarningDeletesDataTitle"
					tag="p"
					class="mb-3 text-base font-bold text-slate-900"
				>
				</i18n-t>

				<i18n-t
					keypath="auth.warningDeletesChangesMessage"
					tag="p"
					class="mb-6 text-sm text-slate-500"
				>
					<strong>{{ t('auth.warningDeleteAction') }}</strong>
					<span class="text-red-500"
						><strong>{{ t('auth.cannotBeUndone') }}</strong></span
					>
				</i18n-t>

				<div v-if="showBox">
					<i18n-t
						keypath="auth.typeDeleteMessage"
						tag="div"
						class="text-sm font-bold text-slate-500 underline"
					>
						<span class="text-red-400 uppercase">{{
							t('auth.deleteValidation')
						}}</span>
					</i18n-t>
					<p class="w-full pt-1 text-sm font-normal text-slate-500">
						{{ pendingNamesList }}
					</p>
				</div>
			</div>
		</template>

		<template #buttons>
			<div class="flex w-full flex-col gap-y-2">
				<DeleteConfirmBox
					@click-delete="(value) => (showBox = value)"
					@confirm-logout="handleConfirm"
				/>

				<RegularButton
					@click="emit('update:modelValue', false)"
					class="bg-accent-400 text-accent-700 border-accent-400 hover:bg-accent-500 w-full justify-center rounded-md border px-5 py-2 font-bold hover:text-white"
				>
					{{ t('auth.closeAndSync') }}
				</RegularButton>
			</div>
		</template>
	</PrecoaModal>
	<LegacyLogoutModal
		v-else
		:model-value="props.modelValue"
		:has-unsent-or-failed="props.hasUnsentOrFailed"
		@update:model-value="emit('update:modelValue', false)"
		@confirm="handleConfirm"
	>
	</LegacyLogoutModal>
</template>

<script setup lang="ts">
import DeleteConfirmBox from '@/components/common/DeleteConfirmBox.vue'
import LegacyLogoutModal from '@/components/common/LegacyLogoutModal.vue'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import RegularButton from '@/components/forms/RegularButton.vue'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { Feature, useFeatureService } from '@/services/featureService'
import { usePlanStore } from '@/stores/plan'
import { useSubmissionStore } from '@/stores/submission'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
	modelValue: boolean
	hasUnsentOrFailed: boolean
}>()

const showBox = ref(false)

const emit = defineEmits<{
	(e: 'update:modelValue', value: boolean): void
	(e: 'confirm'): void
}>()

const cloudSyncService = useCloudSyncService()
const planStore = usePlanStore()
const submissionStore = useSubmissionStore()

onMounted(async () => {
	const pendingPlanIds = await uniquePlanIdsFromBothCloudSyncAndSubmissions()
	await updatePendingNamesList(pendingPlanIds)
})

const uniquePlanIdsFromBothCloudSyncAndSubmissions = async (): Promise<
	string[]
> => {
	const pendingCloudSyncIds =
		await cloudSyncService.getRelatedPlanIdsForPendingUpserts()
	const pendingSubmissionIds = Array.from(
		submissionStore.planHoldersWithFailures
	)
	return Array.from(new Set([...pendingCloudSyncIds, ...pendingSubmissionIds]))
}

const pendingNamesList = ref('...')

const updatePendingNamesList = async (pendingPlanIds: string[]) => {
	try {
		const names = await planStore.getNamesFromPlanIds(pendingPlanIds)
		pendingNamesList.value = names.join(', ')
	} catch (e: any) {
		console.error(e.message)
		pendingNamesList.value = 'Error loading names'
	}
}

function handleConfirm() {
	emit('confirm')
	emit('update:modelValue', false)
}
</script>
